/**
 * panelChrome — the ONE look for a slide-in panel's shell, shared by the
 * calendar's ClosingDetailDrawer and the drilldown's ResizeableSidebar so the
 * two never drift: the paper surface (white, hairline left edge, soft shadow,
 * no dimming scrim), the left-edge resize grip, and the header icon buttons
 * (close / pop-out / delete / upload / …), which are all one 30px square.
 */
import { IconButton, Tooltip, alpha } from "@mui/material";

import { slate, teal } from "../../../theme/colors";
import { C } from "../../closingReadiness/closingReadinessUtils";

/**
 * The width a docked panel opens at — the calendar drawer's default, now the
 * default everywhere so a form pane and the closing drawer are the same size.
 * It's a starting width only: every panel is still drag-resizable from 320px
 * up. `panelWidth()` clamps it (or any requested width) to the viewport so a
 * small laptop never opens a panel wider than its screen.
 */
export const PANEL_DEFAULT_WIDTH = 830;
const PANEL_MIN_WIDTH = 320;
const PANEL_VIEWPORT_MARGIN = 80;

export const panelWidth = (requested = PANEL_DEFAULT_WIDTH) => {
  const vw = typeof window !== "undefined" ? window.innerWidth : 1440;
  return Math.max(
    PANEL_MIN_WIDTH,
    Math.min(requested, vw - PANEL_VIEWPORT_MARGIN),
  );
};

// Paper: white surface, hairline on the docked edge, a soft directional
// shadow instead of a backdrop. `anchor` picks which edge carries the line.
export const panelPaperSx = (anchor = "right") => ({
  background: C.surface,
  ...(anchor === "left"
    ? {
        borderRight: `1px solid ${C.line}`,
        boxShadow: `12px 0 40px -12px ${alpha(slate[900], 0.25)}`,
      }
    : {
        borderLeft: `1px solid ${C.line}`,
        boxShadow: `-12px 0 40px -12px ${alpha(slate[900], 0.25)}`,
      }),
});

// Floating (popped-out) card: same surface, a full hairline + centred shadow.
export const panelFloatingPaperSx = {
  background: C.surface,
  border: `1px solid ${C.line}`,
  boxShadow: `0 12px 40px -12px ${alpha(slate[900], 0.35)}`,
};

// The grab strip on a docked panel's edge — a 3×44 thumb that reads as a line
// at rest, darkens on hover and goes teal while dragging.
export const resizeHandleSx = (active) => ({
  width: 8,
  cursor: "ew-resize",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover .resize-thumb, &:active .resize-thumb": {
    opacity: 1,
    background: active ? teal[500] : C.faint,
  },
});
export const resizeThumbSx = (active) => ({
  width: 3,
  height: 44,
  borderRadius: 2,
  background: active ? teal[500] : C.line,
  opacity: active ? 1 : 0.7,
  transition: "background 0.15s, opacity 0.15s",
  pointerEvents: "none",
});

// Header icon buttons: 30px square, 8px radius, faint ink that darkens on a
// soft tinted hover; disabled fades to the hairline colour.
export const PANEL_ICON_BUTTON_SX = {
  width: 30,
  height: 30,
  borderRadius: "8px",
  color: C.faint,
  "&:hover": { background: C.lineSoft, color: C.ink },
  "&.Mui-disabled": { color: C.line },
};
// Every glyph inside one of those buttons is this size.
export const PANEL_ICON_SIZE = 17;

/**
 * PanelIconButton — a header control in the panel chrome. Pass the icon
 * COMPONENT (`icon={CloseRounded}`), a tooltip `title`, and the click; the
 * button sizes, colours and hovers itself. `sx` is merged last for the rare
 * per-button override (e.g. a red hover on a delete).
 */
export const PanelIconButton = ({
  icon: Icon,
  title,
  onClick,
  disabled,
  sx,
  tooltipPlacement,
  "aria-label": ariaLabel,
  ...rest
}) => {
  const button = (
    <IconButton
      size="small"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || title}
      sx={{ ...PANEL_ICON_BUTTON_SX, ...sx }}
      {...rest}
    >
      <Icon sx={{ fontSize: PANEL_ICON_SIZE }} />
    </IconButton>
  );
  if (!title) return button;
  return (
    <Tooltip title={title} placement={tooltipPlacement} arrow>
      {/* span keeps the tooltip alive while the button is disabled */}
      <span style={{ display: "inline-flex" }}>{button}</span>
    </Tooltip>
  );
};
